var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { uniq } from "lodash";
import hash from "object-hash";
import { ERentioFormField } from "../../components/components/Form";
import { CONFIG } from "../../config/app.config";
import { getValue, getValueOrExtraValue, } from "../../redux/form/form.utils";
import { NON_USER_SELECTABLE_DOCUMENT_CATEGORIES } from "../../reduxV2/documents/document.constants";
import * as documentSelectors from "../../reduxV2/documents/document.selectors";
import { getCategoriesSortedByTranslatedLabel, getDocumentCategoryName, } from "../../reduxV2/documents/document.utils";
import { EContractCostsAreIndexable, EContractPaydate, EContractPaymentRepetitionType, ENoVatType, } from "../../types/contract.types";
import { EPaymentOrderType } from "../../types/payment.types";
import { formatDate } from "../../utils/date.utils";
import { getLocalizedText } from "../../utils/i18n/i18n.utils";
import { formatCurrency } from "../../utils/number.utils";
import { getVatFieldsForSchema, getVatFieldsForUiSchema, } from "../createContract/schema.createContract.utils.vat";
import { EField } from "./schema.createPayment.types";
import { checkIfTypeIsAdditionalCost, checkIfTypeIsOther, getAdditionalCostsSummary, getManagementFeeBrokerInfo, getManagementFeeBrokerPercentage, getManagementFeeBrokerSummaryValue, getNavigationTitle, getRelatedContract, getSelectedAdditionalCost, isRentOrCost, } from "./schema.createPayment.utils";
export var shouldShowCommonCosts = function (formData, contract) {
    if (!contract) {
        return false;
    }
    if ((contract.currentCostsCommon || 0) === 0) {
        return false;
    }
    var type = formData[EField.TypeRecurring] || "-";
    // return EPaymentOrderType.RentAndCosts === type;
    return [
        EPaymentOrderType.CommonCosts,
        EPaymentOrderType.RentAndCosts,
    ].includes(type);
};
export var shouldShowNonCommonCosts = function (formData, contract) {
    if (!contract) {
        return false;
    }
    if ((contract.currentCostsNonCommon || 0) === 0) {
        return false;
    }
    var type = formData[EField.TypeRecurring] || "-";
    // return EPaymentOrderType.RentAndCosts === type;
    return [
        EPaymentOrderType.NonCommonCosts,
        EPaymentOrderType.RentAndCosts,
    ].includes(type);
};
export var shouldShowAdditionalCosts = function (formData, contract) {
    if (!contract) {
        return false;
    }
    var additionalCosts = contract.additionalCosts || [];
    if (additionalCosts.length === 0) {
        return false;
    }
    var type = formData[EField.TypeRecurring] || "-";
    return type === EPaymentOrderType.RentAndCosts;
};
export var getSchema = function (initialValues, extraData) {
    return function (defaultValues) {
        var _a, _b, _c, _d, _e, _f;
        var categoryIds = getCategoriesSortedByTranslatedLabel().filter(function (x) { return !NON_USER_SELECTABLE_DOCUMENT_CATEGORIES.includes(x); });
        var contract = getRelatedContract(extraData, defaultValues);
        var isTypeAdditionalCost = checkIfTypeIsAdditionalCost(defaultValues);
        var selectedAdditionalCost = isTypeAdditionalCost
            ? getSelectedAdditionalCost({
                contract: contract,
                formData: defaultValues,
            })
            : undefined;
        return {
            required: [],
            properties: __assign(__assign(__assign(__assign(__assign(__assign((_a = {}, _a[EField.Header + "2"] = {
                default: undefined,
            }, _a[EField.Info] = {
                default: undefined,
                // @ts-ignore
                showIf: function (formData) { return !!formData[EField.TypeRecurring]; },
            }, _a[EField.ManagementFeeBrokerInfo + "2"] = {
                default: undefined,
                // @ts-ignore
                showIf: function () {
                    return !!extraData.paymentOrder &&
                        getManagementFeeBrokerInfo(extraData) != null;
                },
                // Recompute the banner percentage as the amount changes. Only the
                // management costs order's amount affects it; editing any other
                // payment order type leaves the banner untouched.
                // @ts-ignore
                handleChange: function (fieldName, fieldValue, _myFieldValue, formData) {
                    var _a;
                    var editedType = formData[EField.TypeRecurring] || formData[EField.TypeOnce];
                    if (fieldName !== EField.Price ||
                        editedType !== EPaymentOrderType.ManagementCosts) {
                        return { shouldSetNewValue: false };
                    }
                    return {
                        shouldSetNewValue: true,
                        newValue: getManagementFeeBrokerInfo(extraData, __assign(__assign({}, formData), (_a = {}, _a[fieldName] = fieldValue, _a))),
                    };
                },
            }, _a[EField.OtherDescription] = {
                default: selectedAdditionalCost
                    ? selectedAdditionalCost.description
                    : getValue(EField.OtherDescription, initialValues, defaultValues),
                // @ts-ignore
                showIf: checkIfTypeIsOther,
                // @ts-ignore
                requiredIf: checkIfTypeIsOther,
            }, _a[EField.PaymentRepetition] = {
                default: contract === null || contract === void 0 ? void 0 : contract.repetitionType,
                // @ts-ignore
                options: [
                    EContractPaymentRepetitionType.Monthly,
                    EContractPaymentRepetitionType.Quarterly,
                    EContractPaymentRepetitionType.Yearly,
                ].map(function (id) { return ({
                    id: id,
                    value: getLocalizedText("contract.payment_repetition.option.".concat(id).toLowerCase()),
                }); }),
                // @ts-ignore
                showIf: function (formData) { return !!formData[EField.TypeRecurring]; },
                requiredIf: function (formData) { return !!formData[EField.TypeRecurring]; },
            }, _a[EField.Price] = {
                exclusiveMinimum: 0,
                default: (function () {
                    if (selectedAdditionalCost) {
                        return selectedAdditionalCost.currentPrice;
                    }
                    if (contract) {
                        switch (defaultValues[EField.TypeRecurring]) {
                            case EPaymentOrderType.RentAndCosts:
                            case EPaymentOrderType.Rent:
                                return contract.currentPrice || 0;
                            case EPaymentOrderType.CommonCosts:
                                return contract.currentCostsCommon || 0;
                            case EPaymentOrderType.NonCommonCosts:
                                return contract.currentCostsNonCommon || 0;
                            default:
                                break;
                        }
                    }
                    return getValue(EField.Price, initialValues, defaultValues);
                })(),
                // @ts-ignore
                requiredIf: function (formData) {
                    var type = formData[EField.TypeRecurring] || "-";
                    return ![
                        EPaymentOrderType.CommonCosts,
                        EPaymentOrderType.NonCommonCosts,
                    ].includes(type);
                },
                // @ts-ignore
                showIf: function (formData) {
                    var type = formData[EField.TypeRecurring] || "-";
                    return ![
                        EPaymentOrderType.CommonCosts,
                        EPaymentOrderType.NonCommonCosts,
                    ].includes(type);
                },
            }, _a), getVatFieldsForSchema({
                defaultValues: defaultValues,
                initialValues: initialValues,
                fieldKey: EField.PriceVat,
                infoFieldKey: EField.PriceVatHelpInfo,
                baseAmountFieldKey: EField.Price,
                defaultValue: (function () {
                    if (selectedAdditionalCost) {
                        var priceVat = selectedAdditionalCost.priceVat || 0;
                        if (priceVat === 0) {
                            return (selectedAdditionalCost.priceNoVatType ||
                                ENoVatType.NotOnDeclaration);
                        }
                        return priceVat;
                    }
                    if (contract) {
                        switch (defaultValues[EField.TypeRecurring] ||
                            defaultValues[EField.TypeOnce]) {
                            case EPaymentOrderType.RentAndCosts:
                            case EPaymentOrderType.Rent:
                                var priceVat = contract.priceVat || 0;
                                if (priceVat === 0) {
                                    return (contract.priceNoVatType || ENoVatType.NotOnDeclaration);
                                }
                                return priceVat;
                            case EPaymentOrderType.CommonCosts:
                                var costsCommonVat = contract.costsCommonVat || 0;
                                if (costsCommonVat === 0) {
                                    return (contract.costsCommonNoVatType ||
                                        ENoVatType.NotOnDeclaration);
                                }
                                return costsCommonVat;
                            case EPaymentOrderType.NonCommonCosts:
                                var costsNonCommonVat = contract.costsNonCommonVat || 0;
                                if (costsNonCommonVat === 0) {
                                    return (contract.costsNonCommonNoVatType ||
                                        ENoVatType.NotOnDeclaration);
                                }
                                return costsNonCommonVat;
                            case EPaymentOrderType.BrokerFee:
                            case EPaymentOrderType.ManagementCosts:
                            case EPaymentOrderType.Rentio:
                            case EPaymentOrderType.LocationDescription:
                                return getValueOrExtraValue(EField.PriceVat, defaultValues, 21, false);
                            default:
                                break;
                        }
                    }
                    return getValue(EField.PriceVat, initialValues, defaultValues);
                })(),
                showIf: function (formData) {
                    var type = formData[EField.TypeRecurring] || "-";
                    return ![
                        EPaymentOrderType.CommonCosts,
                        EPaymentOrderType.NonCommonCosts,
                    ].includes(type);
                },
                bookkeepingEnabled: extraData.bookkeepingEnabled,
            })), (_b = {}, _b[EField.DiscountAmount] = {
                default: (contract === null || contract === void 0 ? void 0 : contract.rentDiscountAmount)
                    ? "".concat(contract === null || contract === void 0 ? void 0 : contract.rentDiscountAmount)
                    : 0,
                // @ts-ignore
                showIf: function (formData) {
                    if (!(contract === null || contract === void 0 ? void 0 : contract.rentDiscountEnabled)) {
                        return false;
                    }
                    var type = formData[EField.TypeRecurring] || "-";
                    return [
                        EPaymentOrderType.RentAndCosts,
                        EPaymentOrderType.Rent,
                    ].includes(type);
                },
            }, _b[EField.PriceCommonCosts] = {
                default: contract ? contract.currentCostsCommon || 0 : 0,
                // @ts-ignore
                requiredIf: function (formData) {
                    return shouldShowCommonCosts(formData, contract);
                },
                showIf: function (formData) { return shouldShowCommonCosts(formData, contract); },
            }, _b)), getVatFieldsForSchema({
                defaultValues: defaultValues,
                initialValues: initialValues,
                fieldKey: EField.PriceCommonCostsVat,
                infoFieldKey: EField.PriceCommonCostsVatHelpInfo,
                baseAmountFieldKey: EField.PriceCommonCosts,
                defaultValue: (function () {
                    var costsCommonVat = (contract === null || contract === void 0 ? void 0 : contract.costsCommonVat) || 0;
                    if (costsCommonVat === 0) {
                        return ((contract === null || contract === void 0 ? void 0 : contract.costsCommonNoVatType) || ENoVatType.NotOnDeclaration);
                    }
                    return costsCommonVat;
                })(),
                showIf: function (formData) { return shouldShowCommonCosts(formData, contract); },
                bookkeepingEnabled: extraData.bookkeepingEnabled,
            })), (_c = {}, _c[EField.CommonCostsCurrentAreIndexable] = {
                default: (contract === null || contract === void 0 ? void 0 : contract.commonCostsAreIndexable)
                    ? EContractCostsAreIndexable.Yes
                    : EContractCostsAreIndexable.No,
                showIf: function (formData) { return shouldShowCommonCosts(formData, contract); },
                options: [
                    EContractCostsAreIndexable.Yes,
                    EContractCostsAreIndexable.No,
                ].map(function (id) { return ({
                    id: id,
                    value: getLocalizedText("contract_info.costs.are_indexable.value.".concat(id.toLowerCase())),
                }); }),
            }, _c[EField.PriceNonCommonCosts] = {
                default: contract ? contract.currentCostsNonCommon || 0 : 0,
                // @ts-ignore
                showIf: function (formData) {
                    return shouldShowNonCommonCosts(formData, contract);
                },
                requiredIf: function (formData) {
                    return shouldShowNonCommonCosts(formData, contract);
                },
            }, _c)), getVatFieldsForSchema({
                defaultValues: defaultValues,
                initialValues: initialValues,
                fieldKey: EField.PriceNonCommonCostsVat,
                infoFieldKey: EField.PriceNonCommonCostsVatHelpInfo,
                baseAmountFieldKey: EField.PriceNonCommonCosts,
                defaultValue: (function () {
                    var costsNonCommonVat = (contract === null || contract === void 0 ? void 0 : contract.costsNonCommonVat) || 0;
                    if (costsNonCommonVat === 0) {
                        return ((contract === null || contract === void 0 ? void 0 : contract.costsNonCommonNoVatType) || ENoVatType.NotOnDeclaration);
                    }
                    return costsNonCommonVat;
                })(),
                showIf: function (formData) {
                    return shouldShowNonCommonCosts(formData, contract);
                },
                bookkeepingEnabled: extraData.bookkeepingEnabled,
            })), (_d = {}, _d[EField.NonCommonCostsCurrentAreIndexable] = {
                default: (contract === null || contract === void 0 ? void 0 : contract.nonCommonCostsAreIndexable)
                    ? EContractCostsAreIndexable.Yes
                    : EContractCostsAreIndexable.No,
                showIf: function (formData) {
                    return shouldShowNonCommonCosts(formData, contract);
                },
                options: [
                    EContractCostsAreIndexable.Yes,
                    EContractCostsAreIndexable.No,
                ].map(function (id) { return ({
                    id: id,
                    value: getLocalizedText("contract_info.costs.are_indexable.value.".concat(id.toLowerCase())),
                }); }),
            }, _d[EField.AdditionalCostsArray] = {
                // @ts-ignore
                default: (function () {
                    return ((contract === null || contract === void 0 ? void 0 : contract.additionalCosts) || []).map(function (cost) {
                        var _a;
                        return (_a = {},
                            _a[EField.AdditionalCostPrice] = cost.currentPrice || 0,
                            _a[EField.AdditionalCostPriceVat] = (function () {
                                var priceVat = cost.priceVat || 0;
                                if (priceVat === 0) {
                                    return cost.priceNoVatType || ENoVatType.NotOnDeclaration;
                                }
                                return priceVat;
                            })(),
                            _a[EField.AdditionalCostAreIndexable] = cost.isIndexable
                                ? EContractCostsAreIndexable.Yes
                                : EContractCostsAreIndexable.No,
                            _a.description = cost.description,
                            _a.priceType = cost.priceType,
                            _a.priceVat = cost.priceVat,
                            _a.id = hash(cost),
                            _a);
                    });
                })(),
                type: "array",
                // @ts-ignore
                showIf: function (formData) {
                    return shouldShowAdditionalCosts(formData, contract);
                },
                items: {
                    type: "object",
                    properties: __assign(__assign((_e = {}, _e[EField.AdditionalCostPrice] = {
                        default: undefined,
                    }, _e), getVatFieldsForSchema({
                        defaultValues: defaultValues,
                        initialValues: initialValues,
                        fieldKey: EField.AdditionalCostPriceVat,
                        infoFieldKey: EField.AdditionalCostPriceVatHelpInfo,
                        baseAmountFieldKey: EField.AdditionalCostPrice,
                        bookkeepingEnabled: extraData.bookkeepingEnabled,
                    })), (_f = {}, _f[EField.AdditionalCostAreIndexable] = {
                        // @ts-ignore
                        options: [
                            EContractCostsAreIndexable.Yes,
                            EContractCostsAreIndexable.No,
                        ].map(function (id) { return ({
                            id: id,
                            value: getLocalizedText("contract_info.costs.are_indexable.value.".concat(id.toLowerCase())),
                        }); }),
                    }, _f)),
                },
                uniqueItems: false,
            }, _d[EField.IsIndexable] = {
                default: (selectedAdditionalCost === null || selectedAdditionalCost === void 0 ? void 0 : selectedAdditionalCost.isIndexable)
                    ? EContractCostsAreIndexable.Yes
                    : getValue(EField.IsIndexable, initialValues, defaultValues),
                showIf: function (formData) {
                    return isTypeAdditionalCost ||
                        [
                            EPaymentOrderType.ManagementCosts,
                            EPaymentOrderType.Rentio,
                            EPaymentOrderType.OtherRecurring,
                        ].includes(formData[EField.TypeRecurring]);
                },
                options: [
                    EContractCostsAreIndexable.Yes,
                    EContractCostsAreIndexable.No,
                ].map(function (id) { return ({
                    id: id,
                    value: getLocalizedText("contract_info.costs.are_indexable.value.".concat(id.toLowerCase())),
                }); }),
            }, _d[EField.Document] = {
                default: getValue(EField.Document, initialValues, defaultValues),
                // @ts-ignore
                showIf: function (formData) { return !!formData[EField.TypeOnce]; },
            }, _d[EField.LinkToContract] = {
                default: getValue(EField.LinkToContract, initialValues, defaultValues),
                options: ["yes", "no"].map(function (id) { return ({
                    id: id,
                    value: getLocalizedText("contract_info.costs.are_indexable.value.".concat(id.toLowerCase())),
                }); }),
                showIf: function (formData) {
                    var _a;
                    return !!formData[EField.TypeOnce] &&
                        !!formData[EField.Document] &&
                        typeof ((_a = formData[EField.Document]) === null || _a === void 0 ? void 0 : _a[0]) !== "string";
                },
                requiredIf: function (formData) {
                    var _a;
                    return !!formData[EField.TypeOnce] &&
                        !!formData[EField.Document] &&
                        typeof ((_a = formData[EField.Document]) === null || _a === void 0 ? void 0 : _a[0]) !== "string";
                },
            }, _d[EField.DocumentCategory] = {
                default: getValue(EField.DocumentCategory, initialValues, defaultValues),
                options: uniq(categoryIds).map(function (categoryId) { return ({
                    value: getDocumentCategoryName(categoryId),
                    id: categoryId,
                }); }),
                showIf: function (formData) {
                    var _a;
                    return !!formData[EField.TypeOnce] &&
                        !!formData[EField.Document] &&
                        typeof ((_a = formData[EField.Document]) === null || _a === void 0 ? void 0 : _a[0]) !== "string";
                },
                requiredIf: function (formData) {
                    var _a;
                    return !!formData[EField.TypeOnce] &&
                        !!formData[EField.Document] &&
                        typeof ((_a = formData[EField.Document]) === null || _a === void 0 ? void 0 : _a[0]) !== "string";
                },
            }, _d[EField.ManagementFeeBrokerInfo] = {
                default: undefined,
                // @ts-ignore
                showIf: function () { return getManagementFeeBrokerPercentage(extraData) != null; },
            }, _d)),
        };
    };
};
export var uiSchema = function (initialValues, extraData) {
    var _a, _b, _c, _d, _e, _f;
    var getPriceTypeCommonCosts = function (contract) {
        return getLocalizedText("payment.payment_order.payment_type.value.".concat((contract === null || contract === void 0 ? void 0 : contract.priceTypeCommonCosts) || "fixed").toLowerCase());
    };
    var getPriceTypeNonCommonCosts = function (contract) {
        return getLocalizedText("payment.payment_order.payment_type.value.".concat((contract === null || contract === void 0 ? void 0 : contract.priceTypeNonCommonCosts) || "fixed").toLowerCase());
    };
    return __assign(__assign(__assign(__assign(__assign(__assign((_a = { "rentio:title": getLocalizedText("payments.create.first_payment.heading.summary"), "rentio:navigationTitle": getNavigationTitle(extraData), "ui:field": ERentioFormField.Grid, grid: {
                row: [
                    {
                        col: {
                            width: 1,
                            children: [EField.Header + "6"],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.Info],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.ManagementFeeBrokerInfo + "2"],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.OtherDescription],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.PaymentRepetition],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.Price],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.PriceVat],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.DiscountAmount],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.Document],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.LinkToContract],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.DocumentCategory],
                        },
                    },
                    {
                        col: {
                            width: [1, 4 / 12],
                            children: [EField.PriceCommonCosts],
                        },
                    },
                    {
                        col: {
                            width: [1, 4 / 12],
                            children: [EField.PriceCommonCostsVat],
                        },
                    },
                    {
                        col: {
                            width: [1, 4 / 12],
                            children: [EField.CommonCostsCurrentAreIndexable],
                        },
                    },
                    {
                        col: {
                            width: [1, 4 / 12],
                            children: [EField.PriceNonCommonCosts],
                        },
                    },
                    {
                        col: {
                            width: [1, 4 / 12],
                            children: [EField.PriceNonCommonCostsVat],
                        },
                    },
                    {
                        col: {
                            width: [1, 4 / 12],
                            children: [EField.NonCommonCostsCurrentAreIndexable],
                        },
                    },
                    {
                        col: {
                            width: 1,
                            children: [EField.AdditionalCostsArray],
                        },
                    },
                    {
                        col: {
                            width: [1, 6 / 12],
                            children: [EField.IsIndexable],
                        },
                    },
                ],
            } }, _a[EField.Header + "2"] = {
        "ui:field": ERentioFormField.SectionHeader,
        getTitle: function (formData) {
            return getLocalizedText("payments.create.first_payment.heading", {
                type: getLocalizedText("payment.payment_order.type.".concat(formData[EField.TypeRecurring] || formData[EField.TypeOnce]).toLowerCase()),
            });
        },
    }, _a[EField.Info] = {
        "ui:field": ERentioFormField.HelpInfo,
        title: getLocalizedText("payment.payment_order.contract_info.heading"),
        getSubtitle: function (state, formData) {
            var contract = getRelatedContract(extraData, formData);
            if (!contract) {
                return getLocalizedText("payments.create.add_costs.no_contract.content");
            }
            var commonCostsCopy = contract.currentCostsCommon
                ? getLocalizedText("payment.payment_order.contract_info.content.basic.common_costs", {
                    commonCosts: formatCurrency(contract.currentCostsCommon),
                })
                : null;
            var nonCommonCostsCopy = contract.currentCostsNonCommon
                ? getLocalizedText("payment.payment_order.contract_info.content.basic.non_common_costs", {
                    nonCommonCosts: formatCurrency(contract.currentCostsNonCommon),
                })
                : null;
            var costsArray = [commonCostsCopy, nonCommonCostsCopy].filter(Boolean);
            return getLocalizedText("payment.payment_order.contract_info.content.basic".concat(contract.stopDate ? "" : ".no_end_date"), {
                startDate: formatDate(contract.startDate),
                stopDate: contract.stopDate ? formatDate(contract.stopDate) : "",
                amount: formatCurrency(contract.currentPrice),
                costs: costsArray.length > 0
                    ? " + ".concat(costsArray.join(" ".concat(getLocalizedText("system.and"), " ")))
                    : "",
                payDate: contract.payDateStart === EContractPaydate.Calendar
                    ? getLocalizedText("contract.paydate.before_month")
                    : getLocalizedText("contract.paydate.before_period"),
            });
        },
    }, _a[EField.ManagementFeeBrokerInfo + "2"] = {
        "ui:field": ERentioFormField.HelpInfo,
        title: getLocalizedText("payment.payment_order.management_fixed.heading.summary"),
        // The banner value is recomputed by the field's handleChange as the amount
        // changes; fall back to the initial computed value for the first render.
        getSubtitle: function (_state, _formData, value) {
            return value !== null && value !== void 0 ? value : getManagementFeeBrokerInfo(extraData);
        },
    }, _a[EField.OtherDescription] = {
        "ui:field": ERentioFormField.Text,
        title: getLocalizedText("payment.payment_order.other_description.label"),
        placeholder: getLocalizedText("payment.payment_order.other_description.label"),
        autoFocus: true,
        isDisabled: function (formData) { return checkIfTypeIsAdditionalCost(formData); },
    }, _a[EField.PaymentRepetition] = {
        "ui:field": ERentioFormField.InlineSelect,
        title: getLocalizedText("contract.payment_repetition.option.label"),
        formatSummary: function (value) {
            return getLocalizedText("contract.payment_repetition.option.".concat(value).toLowerCase());
        },
        isDisabled: function (_) { return true; },
    }, _a[EField.Price] = {
        "ui:field": ERentioFormField.Number,
        formatSummary: function (formData) {
            return formData && formatCurrency(formData);
        },
        title: getLocalizedText("payment.payment_order.amount"),
        placeholder: getLocalizedText("payment.payment_order.amount"),
        suffixText: "EUR",
        isFloat: true,
        decimals: CONFIG.DECIMALS_FOR_CURRENCY,
        isDisabled: function (formData) {
            return checkIfTypeIsAdditionalCost(formData) ||
                isRentOrCost(formData[EField.TypeRecurring]);
        },
        autoFocus: true,
    }, _a), getVatFieldsForUiSchema({
        fieldKey: EField.PriceVat,
        infoFieldKey: EField.PriceVatHelpInfo,
        baseAmountFieldKey: EField.Price,
        isDisabled: function (formData) {
            return checkIfTypeIsAdditionalCost(formData) ||
                !!isRentOrCost(formData[EField.TypeRecurring]);
        },
    })), (_b = {}, _b[EField.DiscountAmount] = {
        "ui:field": ERentioFormField.Text,
        formatSummary: function (formData) {
            return formData && formatCurrency(formData);
        },
        title: getLocalizedText("contract.discount.amount.label"),
        getInfo: function (formData) {
            var contract = getRelatedContract(extraData, formData);
            return getLocalizedText("contract.discount.amount.info_extended", {
                amount: "".concat(formatCurrency((contract === null || contract === void 0 ? void 0 : contract.rentDiscountAmount) || 0)),
                offset: "".concat((contract === null || contract === void 0 ? void 0 : contract.rentDiscountOffset) || 0),
            }, (contract === null || contract === void 0 ? void 0 : contract.rentDiscountOffset) || 0);
        },
        suffixText: "EUR",
        hideOnSummaryIfEmpty: true,
        isDisabled: function () { return true; },
    }, _b[EField.PriceCommonCosts] = {
        "ui:field": ERentioFormField.Number,
        formatSummary: function (formData) {
            return formData && formatCurrency(formData);
        },
        getTitle: function (formData) {
            var contract = getRelatedContract(extraData, formData);
            return getLocalizedText("payment.payment_order.common_costs", {
                priceType: getPriceTypeCommonCosts(contract),
            });
        },
        getPlaceholder: function (formData) {
            var contract = getRelatedContract(extraData, formData);
            return getLocalizedText("payment.payment_order.common_costs", {
                priceType: getPriceTypeCommonCosts(contract),
            });
        },
        suffixText: "EUR",
        isFloat: true,
        decimals: CONFIG.DECIMALS_FOR_CURRENCY,
        isDisabled: function () { return true; },
    }, _b)), getVatFieldsForUiSchema({
        fieldKey: EField.PriceCommonCostsVat,
        infoFieldKey: EField.PriceCommonCostsVatHelpInfo,
        baseAmountFieldKey: EField.PriceCommonCosts,
        isDisabled: function () { return true; },
    })), (_c = {}, _c[EField.CommonCostsCurrentAreIndexable] = {
        "ui:field": ERentioFormField.SegmentedControl,
        title: getLocalizedText("contract_info.common_costs.are_indexable.label"),
        formatSummary: function (formData) {
            return formData &&
                getLocalizedText("contract_info.costs.are_indexable.value.".concat(formData.toLowerCase()));
        },
        isDisabled: function () { return true; },
        shouldShowOnSummary: function (_) { return true; },
    }, _c[EField.PriceNonCommonCosts] = {
        "ui:field": ERentioFormField.Number,
        formatSummary: function (formData) {
            return formData && formatCurrency(formData);
        },
        getTitle: function (formData) {
            var contract = getRelatedContract(extraData, formData);
            return getLocalizedText("payment.payment_order.non_common_costs", {
                priceType: getPriceTypeNonCommonCosts(contract),
            });
        },
        getPlaceholder: function (formData) {
            var contract = getRelatedContract(extraData, formData);
            return getLocalizedText("payment.payment_order.non_common_costs", {
                priceType: getPriceTypeNonCommonCosts(contract),
            });
        },
        suffixText: "EUR",
        isFloat: true,
        decimals: CONFIG.DECIMALS_FOR_CURRENCY,
        isDisabled: function () { return true; },
    }, _c)), getVatFieldsForUiSchema({
        fieldKey: EField.PriceNonCommonCostsVat,
        infoFieldKey: EField.PriceNonCommonCostsVatHelpInfo,
        baseAmountFieldKey: EField.PriceNonCommonCosts,
        isDisabled: function () { return true; },
    })), (_d = {}, _d[EField.NonCommonCostsCurrentAreIndexable] = {
        "ui:field": ERentioFormField.SegmentedControl,
        title: getLocalizedText("contract_info.non_common_costs.are_indexable.label"),
        formatSummary: function (formData) {
            return formData &&
                getLocalizedText("contract_info.costs.are_indexable.value.".concat(formData.toLowerCase()));
        },
        isDisabled: function () { return true; },
        shouldShowOnSummary: function (_) { return true; },
    }, _d[EField.AdditionalCostsArray] = {
        "ui:field": ERentioFormField.Array,
        "ui:options": {
            orderable: false,
            addable: false,
            removable: false,
        },
        title: getLocalizedText("contract_info.form.additional_costs.label"),
        items: __assign(__assign((_e = { "ui:field": ERentioFormField.Grid, grid: {
                    row: [
                        {
                            col: {
                                width: [1, 4 / 12],
                                children: [EField.AdditionalCostPrice],
                            },
                        },
                        {
                            col: {
                                width: [1, 4 / 12],
                                children: [EField.AdditionalCostPriceVat],
                            },
                        },
                        {
                            col: {
                                width: [1, 4 / 12],
                                children: [EField.AdditionalCostAreIndexable],
                            },
                        },
                        {
                            col: {
                                width: 1,
                                children: [EField.AdditionalCostPriceVatHelpInfo],
                            },
                        },
                    ],
                } }, _e[EField.AdditionalCostPrice] = {
            "ui:field": ERentioFormField.Number,
            getTitle: function (_, itemData) {
                return "".concat(itemData === null || itemData === void 0 ? void 0 : itemData.description, " (").concat(getLocalizedText("contract_info.costs.price_type.value.".concat(itemData.priceType).toLowerCase()), ")");
            },
            suffixText: "EUR",
            isFloat: true,
            decimals: CONFIG.DECIMALS_FOR_CURRENCY,
            isDisabled: function () { return true; },
            formatSummary: function (formData) {
                return formData && formatCurrency(formData);
            },
        }, _e), getVatFieldsForUiSchema({
            fieldKey: EField.AdditionalCostPriceVat,
            infoFieldKey: EField.AdditionalCostPriceVatHelpInfo,
            baseAmountFieldKey: EField.AdditionalCostPrice,
            isDisabled: function () { return true; },
        })), (_f = {}, _f[EField.AdditionalCostAreIndexable] = {
            "ui:field": ERentioFormField.SegmentedControl,
            title: getLocalizedText("contract_info.common_costs.are_indexable.label"),
            formatSummary: function (formData) {
                return formData &&
                    getLocalizedText("contract_info.costs.are_indexable.value.".concat(formData.toLowerCase()));
            },
            isDisabled: function () { return true; },
            shouldShowOnSummary: function (_) { return true; },
        }, _f)),
        formatSummary: function (formData) { return getAdditionalCostsSummary(formData); },
    }, _d[EField.IsIndexable] = {
        "ui:field": ERentioFormField.SegmentedControl,
        title: getLocalizedText("contract_info.common_costs.are_indexable.label"),
        formatSummary: function (formData) {
            return formData &&
                getLocalizedText("contract_info.costs.are_indexable.value.".concat(formData.toLowerCase()));
        },
        shouldShowOnSummary: function (_) { return true; },
        isDisabled: function (formData) { return checkIfTypeIsAdditionalCost(formData); },
    }, _d[EField.Document] = {
        "ui:field": ERentioFormField.DocumentSelectInline,
        title: getLocalizedText("payments.create.document.label"),
        optional: true,
        multiple: false,
        useExistingDocuments: true,
        useNewDocuments: true,
        showOptionsOnFirstFocus: true,
        getExistingDocumentsWhereFilter: function () { return ({
            propertyId: { eq: extraData.property.id },
        }); },
        showIf: function (formData) { return !!formData[EField.TypeOnce]; },
        isDisabled: function () { return !!extraData.paymentOrder; },
        valueSelector: function (state, formData) {
            if (!formData) {
                return undefined;
            }
            var document = formData === null || formData === void 0 ? void 0 : formData[0];
            if (typeof document === "string") {
                var doc = documentSelectors.getDetail(state, document);
                if (doc) {
                    return doc.filename;
                }
                return getLocalizedText("summary.view.document");
            }
            return document.name;
        },
    }, _d[EField.LinkToContract] = {
        "ui:field": ERentioFormField.Switch,
        title: getLocalizedText("payment.payment_order.link_to_contract.label"),
    }, _d[EField.DocumentCategory] = {
        "ui:field": ERentioFormField.DropdownSelect,
        title: getLocalizedText("payment.payment_order.document_category.label"),
        formatSummary: function (categoryId) {
            return categoryId != null ? getDocumentCategoryName(categoryId) : undefined;
        },
    }, _d[EField.ManagementFeeBrokerInfo] = {
        "ui:field": ERentioFormField.Text,
        title: getLocalizedText("payment.payment_order.management_fixed.heading.summary"),
        formatSummary: function (_value, formData) {
            return getManagementFeeBrokerSummaryValue(extraData, formData);
        },
    }, _d));
};
export var getValidate = function (_) { return function (formData, errors) {
    if (errors[EField.OtherDescription]) {
        var otherDescription = (formData[EField.OtherDescription] ||
            "");
        if (otherDescription.length > 0) {
            // Contains only alphanumeric characters & space
            var isValid = CONFIG.PAYOUT_REFERENCE_REGEX.test(otherDescription);
            if (!isValid) {
                errors[EField.OtherDescription].addError(getLocalizedText("payment.payment_order.other_description.error.no_special_characters"));
            }
        }
    }
    return errors;
}; };
